/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState, useCallback } from "react";
import {
    Wrench, Snowflake, Home, Zap, Trees, Paintbrush,
    Sparkles, Bug, Hammer, Shield, Scissors, Droplets,
    Sun, DoorOpen, Square, Layers, Fence, SprayCan,
    Trash2, Key, Brush, Settings, Refrigerator, ChevronLeft, ChevronRight
} from "lucide-react";
import { cn } from "@/lib/utils";

/* 24 home service businesses, 6 per slide, 4 slides.
   EVERY card now uses a real photographic background of the trade in action,
   with a brand-color gradient overlay for legibility and on-palette feel.
   The gradient palette stays anchored to the brand blue with carefully tuned
   variations so the section reads as a cohesive system, not a mood-board. */
const U = (id) => `https://images.unsplash.com/photo-${id}?w=900&q=80&auto=format&fit=crop`;

const TRADES = [
    // Slide 1 — The core trades
    { name: "Plumbing", tagline: "Drains, leaks, water heaters", Icon: Wrench, from: "#0b72d8", to: "#188bf6", photo: U("1607472586893-edb57bdc0e39") },
    { name: "HVAC", tagline: "Heating, cooling, install & service", Icon: Snowflake, from: "#188bf6", to: "#5BA9F8", photo: U("1568634697393-0165d25e7acb") },
    { name: "Roofing", tagline: "Repairs, replacement, inspections", Icon: Home, from: "#0a3d8c", to: "#0b72d8", photo: U("1591474200742-8e512e6f98f8") },
    { name: "Electrical", tagline: "Wiring, panels, troubleshooting", Icon: Zap, from: "#0b72d8", to: "#188bf6", photo: U("1497366216548-37526070297c") },
    { name: "Landscaping", tagline: "Lawn care, design, maintenance", Icon: Trees, from: "#1d8f5a", to: "#3bb87a", photo: U("1416879595882-3373a0480b5b") },
    { name: "Painting", tagline: "Interior, exterior, finishes", Icon: Paintbrush, from: "#7a3ed1", to: "#9a5ce8", photo: U("1562259949-e8e7689d7828") },

    // Slide 2 — Service & maintenance
    { name: "House Cleaning", tagline: "Recurring, deep cleans, move-outs", Icon: Sparkles, from: "#188bf6", to: "#5BA9F8", photo: U("1581578731548-c64695cc6952") },
    { name: "Pest Control", tagline: "Inspection, treatment, prevention", Icon: Bug, from: "#0b72d8", to: "#188bf6", photo: U("1605600659908-0ef719419d41") },
    { name: "General Contractors", tagline: "Remodels, builds, full-scope", Icon: Hammer, from: "#0a3d8c", to: "#0b72d8", photo: U("1585704032915-c3400ca199e7") },
    { name: "Restoration", tagline: "Water, fire, mold damage", Icon: Shield, from: "#c43a3a", to: "#e25555", photo: U("1564540583246-934409427776") },
    { name: "Tree Service", tagline: "Trim, removal, stump grinding", Icon: Scissors, from: "#1d8f5a", to: "#3bb87a", photo: U("1448375240586-882707db888b") },
    { name: "Pool Service", tagline: "Cleaning, chemicals, repairs", Icon: Droplets, from: "#188bf6", to: "#5BA9F8", photo: U("1576013551627-0cc20b96c2a7") },

    // Slide 3 — Exterior & specialty
    { name: "Solar", tagline: "Install, panels, maintenance", Icon: Sun, from: "#e89b1a", to: "#f2b94d", photo: U("1509391366360-2e959784a276") },
    { name: "Garage Doors", tagline: "Install, repair, openers", Icon: DoorOpen, from: "#0b72d8", to: "#188bf6", photo: U("1696992812596-3c0d4d2d1299") },
    { name: "Window Services", tagline: "Install, repair, replacement", Icon: Square, from: "#188bf6", to: "#5BA9F8", photo: U("1602343168117-bb8ffe3e2e9f") },
    { name: "Flooring", tagline: "Hardwood, tile, LVP, install", Icon: Layers, from: "#7a3ed1", to: "#9a5ce8", photo: U("1581622558663-b2e33377dfb2") },
    { name: "Fencing", tagline: "Install, repair, replacement", Icon: Fence, from: "#1d8f5a", to: "#3bb87a", photo: U("1568605114967-8130f3a36994") },
    { name: "Pressure Washing", tagline: "Driveways, siding, decks", Icon: SprayCan, from: "#188bf6", to: "#5BA9F8", photo: U("1558618666-fcd25c85cd64") },

    // Slide 4 — Repair & handy services
    { name: "Junk Removal", tagline: "Hauling, cleanouts, disposal", Icon: Trash2, from: "#c43a3a", to: "#e25555", photo: U("1582408921715-18e7806365c1") },
    { name: "Locksmith", tagline: "Lockouts, rekey, install", Icon: Key, from: "#0a3d8c", to: "#0b72d8", photo: U("1558002038-1055907df827") },
    { name: "Carpet Cleaning", tagline: "Steam, stain, upholstery", Icon: Brush, from: "#7a3ed1", to: "#9a5ce8", photo: U("1558211583-d26f610c1eb1") },
    { name: "Handyman", tagline: "Repairs, install, odd jobs", Icon: Settings, from: "#188bf6", to: "#5BA9F8", photo: U("1581244277943-fe4a9c777189") },
    { name: "Septic", tagline: "Pump, inspection, repair", Icon: Droplets, from: "#1d8f5a", to: "#3bb87a", photo: U("1581092335397-9583eb92d232") },
    { name: "Appliance Repair", tagline: "Fridge, washer, dryer, oven", Icon: Refrigerator, from: "#0b72d8", to: "#188bf6", photo: U("1604335399105-a0c585fd81a1") },
];

const PER_SLIDE = 6;
const SLIDES = Array.from({ length: Math.ceil(TRADES.length / PER_SLIDE) }, (_, i) =>
    TRADES.slice(i * PER_SLIDE, i * PER_SLIDE + PER_SLIDE)
);

/* SVG mini-illustration fallback — only used if photo fails to load.
   Subtle, on-brand visual element behind the icon. */
function CardArt({ from, to }) {
    return (
        <svg
            viewBox="0 0 200 120"
            preserveAspectRatio="none"
            aria-hidden="true"
            className="absolute inset-0 w-full h-full opacity-90 pointer-events-none"
        >
            <defs>
                <linearGradient id={`g-${from.slice(1)}-${to.slice(1)}`} x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor={from} />
                    <stop offset="100%" stopColor={to} />
                </linearGradient>
                <pattern id={`p-${from.slice(1)}`} width="20" height="20" patternUnits="userSpaceOnUse">
                    <circle cx="1" cy="1" r="1" fill="rgba(255,255,255,0.15)" />
                </pattern>
            </defs>
            <rect width="200" height="120" fill={`url(#g-${from.slice(1)}-${to.slice(1)})`} />
            <rect width="200" height="120" fill={`url(#p-${from.slice(1)})`} />
            <circle cx="170" cy="24" r="26" fill="rgba(255,255,255,0.08)" />
            <circle cx="30" cy="100" r="38" fill="rgba(255,255,255,0.06)" />
        </svg>
    );
}

function TradeCard({ trade }) {
    const { name, tagline, Icon, from, to, photo } = trade;
    const [imgFailed, setImgFailed] = useState(false);
    const showPhoto = Boolean(photo) && !imgFailed;
    return (
        <div
            data-testid={`trade-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="group relative rounded-2xl overflow-hidden card-lift transition-transform duration-300 hover:-translate-y-1"
        >
            {showPhoto ? (
                <img
                    src={photo}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    draggable="false"
                    onError={() => setImgFailed(true)}
                    className="absolute inset-0 w-full h-full object-cover select-none transition-transform duration-700 group-hover:scale-105"
                />
            ) : (
                <CardArt from={from} to={to} />
            )}
            {/* Brand-color overlay for legibility + on-palette feel */}
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                    background: `linear-gradient(165deg, ${from}99 0%, ${to}55 40%, rgba(10,11,13,0.72) 100%)`,
                }}
            />
            <div className="relative p-5 sm:p-6 flex flex-col h-full min-h-[150px] sm:min-h-[170px]">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-white/22 backdrop-blur-sm border border-white/35 flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.12)]">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" aria-hidden="true" />
                </div>
                <div className="mt-auto pt-6">
                    <h3 className="font-display font-extrabold text-white text-base sm:text-lg lg:text-xl tracking-tight drop-shadow-[0_1px_4px_rgba(0,0,0,0.45)]">
                        {name}
                    </h3>
                    <p className="mt-1 text-[12px] sm:text-[13px] text-white/95 font-display font-medium leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">
                        {tagline}
                    </p>
                </div>
            </div>
        </div>
    );
}

export function WhoWeWorkWith({ className = "", testId = "who-we-work-with" }) {
    const [index, setIndex] = useState(0);
    const [isHovered, setHovered] = useState(false);
    const slideCount = SLIDES.length;

    const goTo = useCallback((i) => setIndex(((i % slideCount) + slideCount) % slideCount), [slideCount]);
    const next = useCallback(() => setIndex((p) => (p + 1) % slideCount), [slideCount]);
    const prev = useCallback(() => setIndex((p) => (p - 1 + slideCount) % slideCount), [slideCount]);

    useEffect(() => {
        if (isHovered) return;
        const id = setInterval(() => setIndex((p) => (p + 1) % slideCount), 5000);
        return () => clearInterval(id);
    }, [isHovered, slideCount]);

    return (
        <section
            data-testid={testId}
            className={cn("relative bg-white py-14 sm:py-20", className)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 sm:gap-8">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-display font-bold text-[#4a5562]">
                            <span className="inline-block h-[2px] w-7 bg-[#188bf6]" aria-hidden="true" />
                            Who we work with
                        </div>
                        <h2 className="mt-3 sm:mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0a0b0d]">
                            Built for the trades that keep
                            <br />
                            <span className="text-[#188bf6]">neighborhoods running.</span>
                        </h2>
                        <p className="mt-3 sm:mt-4 text-sm sm:text-base text-[#4a5562] max-w-xl leading-relaxed">
                            We've built the Elevara stack around how home
                            service operators actually run their businesses.
                            Dispatch in the morning, follow up in the
                            afternoon, book the next job before dinner.
                            That's the rhythm we design for — across
                            <span className="font-bold text-[#0a0b0d]"> 24+ home service trades</span>.
                        </p>
                    </div>
                    {/* Slide controls (desktop only — mobile just auto-rotates with dots) */}
                    <div className="hidden sm:flex items-center gap-2 self-start sm:self-end">
                        <button
                            type="button"
                            aria-label="Previous slide"
                            data-testid="who-prev-button"
                            onClick={prev}
                            className="w-10 h-10 rounded-full border-2 border-[#0a0b0d]/14 hover:border-[#188bf6] hover:text-[#188bf6] flex items-center justify-center transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            aria-label="Next slide"
                            data-testid="who-next-button"
                            onClick={next}
                            className="w-10 h-10 rounded-full border-2 border-[#0a0b0d]/14 hover:border-[#188bf6] hover:text-[#188bf6] flex items-center justify-center transition-colors"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Slides container */}
                <div
                    className="mt-8 sm:mt-10 relative"
                    data-testid="who-slideshow-container"
                    aria-live="polite"
                >
                    {SLIDES.map((slide, i) => (
                        <div
                            key={`slide-${slide[0].name}`}
                            aria-hidden={i !== index}
                            data-testid={`who-slide-${i}`}
                            className={cn(
                                "transition-opacity duration-700 ease-out",
                                i === index
                                    ? "opacity-100 relative animate-slide-fade-in"
                                    : "opacity-0 pointer-events-none absolute inset-x-0 top-0",
                            )}
                        >
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                                {slide.map((t) => (
                                    <TradeCard key={t.name} trade={t} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Dots */}
                <div
                    className="mt-7 sm:mt-8 flex items-center justify-center gap-2"
                    data-testid="who-slide-dots"
                >
                    {SLIDES.map((slide, i) => (
                        <button
                            key={`dot-${slide[0].name}`}
                            type="button"
                            aria-label={`Go to slide ${i + 1}`}
                            data-testid={`who-slide-dot-${i}`}
                            onClick={() => goTo(i)}
                            className={cn(
                                "h-2 rounded-full transition-all duration-300",
                                i === index
                                    ? "w-8 bg-[#188bf6]"
                                    : "w-2 bg-[#0a0b0d]/20 hover:bg-[#0a0b0d]/35",
                            )}
                        />
                    ))}
                </div>

                <p className="mt-7 text-xs sm:text-sm text-[#4a5562] text-center">
                    Don't see your trade?{" "}
                    <a
                        href="https://grow.theelevara.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="who-we-work-with-book-link"
                        className="text-[#188bf6] font-display font-bold hover:underline"
                    >
                        Book a call
                    </a>{" "}
                    — if you serve homeowners, we probably fit.
                </p>
            </div>
        </section>
    );
}

export default WhoWeWorkWith;
